import React, { useEffect, useState, useCallback } from "react";
import { paymentsApi } from "../services/payments";
import type { PendingBalance } from "../types/payments";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";
import { useAccountCache } from "../hooks/useAccountCache";
import "./PaintSkeleton.css";

// Persistent (or dismissible) banner shown to talents who haven't connected
// Stripe yet but already have admin-created accounts. Pulls platform-held
// earnings via /payments/pending-balance, surfaces a single "Connect Bank"
// CTA that triggers Stripe Connect onboarding.
//
// Used on /profile (dismissible=false — always shown until Stripe connected)
// and /requests (dismissible=true — talent can hide mid-set during a gig).
// Dismiss state is per-device via localStorage; resurfaces on a new browser.
//
// The deferred-Stripe cohort this serves is admin-created Ghost DJ accounts
// (see CLAUDE.md → "Ghost DJ Feature (Future)"). Self-signup has been
// mandatory-Stripe — no new self-signup talent
// can land in deferred state.

interface Props {
  // Always dismissible now (trial-mode info banner with "Got it" button).
  // storageKey is optional — defaults to a single shared key so dismissing
  // on /profile also dismisses on /requests, and vice versa. One "Got it"
  // click and they're done across the whole app.
  storageKey?: string;
}

export default function PendingEarningsBanner({
  storageKey = "pendingEarningsBannerDismissed",
}: Props) {
  // The last answer paints at once on a later visit (no push-down on every
  // tab open); only a banner that is genuinely new eases in.
  const [data, setData, fromCache] = useAccountCache<PendingBalance | null>("reqs-pendingBalance", null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "1") setDismissed(true);
    } catch { /* ignore */ }
  }, [storageKey]);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await paymentsApi.pendingBalance();
      setData(res);
    } catch (err) {
      console.error("PendingEarningsBanner fetch failed:", err);
    }
  }, [setData]);

  useEffect(() => {
    fetchBalance();
    // Poll every 60s when tab is visible. Talent's profile/requests pages may
    // sit open for hours during a gig — we don't want to hammer the API or
    // burn battery polling a hidden tab.
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchBalance();
    }, 60_000);
    return () => clearInterval(t);
  }, [fetchBalance]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, "1");
    } catch { /* ignore */ }
  };

  if (!data) return null;
  if (data.isStripeOnboarded) return null;
  if (dismissed) return null;
  // Show for any deferred-Stripe talent — today that's admin-created Ghost
  // DJ accounts. The server surfaces this via isDeferredStripe; fall back to
  // the underlying flags so the banner still appears against an older server
  // build during a phased deploy.
  const isDeferred =
    data.isDeferredStripe ?? (data.isAdminCreated || !!data.chosenDeferredStripe);
  if (!isDeferred) return null;

  // Informational trial-mode banner. Lives only on /requests now (founder call).
  // The "Connect Bank" CTA lives in the Payouts section on /profile so talents
  // have an obvious place to go when ready.
  return (
    <div
      className={fromCache ? undefined : "psk-arrive"}
      style={{
        background: "linear-gradient(135deg, #16a34a, #15803d)",
        color: "white",
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: "0.85rem",
        fontWeight: 500,
        boxShadow: "0 1px 4px rgba(22, 163, 74, 0.18)",
        flexWrap: "wrap",
        lineHeight: 1.4,
        // Stack above the absolutely-positioned Live/Offline badge on /requests
        // (z-index 1). When the talent dismisses, this returns null → badge
        // becomes visible naturally.
        position: "relative",
        zIndex: 10,
      }}
      role="status"
      aria-live="polite"
    >
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <T k="pendingEarningsBanner.trialModeEarnNow" tags={{ 0: (c) => <strong style={{ fontWeight: 700 }}>{c}</strong> }} />
      </div>
      <button
        onClick={handleDismiss}
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          color: "#15803d",
          border: "none",
          padding: "4px 12px",
          borderRadius: 6,
          fontWeight: 600,
          fontSize: "0.78rem",
          cursor: "pointer",
          flex: "0 0 auto",
          letterSpacing: 0.2,
        }}
      >
        {tr("pendingEarningsBanner.gotIt")}
      </button>
    </div>
  );
}
